import { useState } from "react";
import { api, logout, type Me } from "../api";
import { useAsync } from "../hooks";
import { ConfirmModal } from "./ConfirmModal";

/** One row of GET /session/orgs — the caller's memberships, the active one is the current workspace. */
interface Org {
  tenantId: string;
  tenantName: string;
  active: boolean;
}

/**
 * Owner-only "danger zone": permanently delete THIS workspace (hard, irreversible). Type-to-confirm the
 * workspace name (via ConfirmModal), then DELETE /me/workspace; on success sign out and return to the
 * landing page. Self-gates — renders nothing unless the session is a workspace OWNER.
 */
export function DeleteWorkspace({ me }: { me: Me }) {
  const { data: orgs } = useAsync<Org[]>(() => api("/session/orgs"), []);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (me.tenantRole !== "owner") return null;
  const current = orgs?.find((o) => o.active) ?? orgs?.find((o) => o.tenantId === me.tenantId);
  const name = current?.tenantName ?? "";
  // A workspace delete is workspace-scoped, not account closure: the last one can't be deleted.
  // `orgs === null` = still loading (don't flash the note); the server enforces this too.
  const isOnlyWorkspace = orgs !== null && orgs.length <= 1;

  async function doDelete() {
    setBusy(true);
    setErr(null);
    try {
      const res = await api<{ deleted: boolean; switchedTo: string | null }>("/me/workspace", {
        method: "DELETE",
        body: JSON.stringify({ confirmName: name }),
      });
      // If we still belong to another workspace, the server re-issued the session for it — keep that
      // session and reload into it. Only when this was the LAST workspace do we sign out.
      if (!res.switchedTo) await logout();
      window.location.href = "/";
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to delete the workspace");
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <section className="stack-sm">
      <h3>Danger zone</h3>
      <p className="muted sm">
        Permanently delete <b>{name || "this workspace"}</b> and everything in it — scrapers, runs,
        results, team, and API keys. This cannot be undone.
      </p>
      {err && <p className="err sm">{err}</p>}
      {isOnlyWorkspace ? (
        <p className="muted sm">
          This is your only workspace, so it can't be deleted — create another one first.
        </p>
      ) : (
        <div>
          <button className="btn danger sm" disabled={!name} onClick={() => setOpen(true)}>
            Delete workspace…
          </button>
        </div>
      )}
      {open && (
        <ConfirmModal
          title="Delete this workspace?"
          body={
            <>
              This permanently deletes <b>{name}</b> and all of its data. If it's your last workspace
              you'll be signed out; otherwise you'll be moved to another one. Type the workspace name
              to confirm.
            </>
          }
          confirmPhrase={name}
          confirmLabel="Permanently delete"
          danger
          busy={busy}
          onConfirm={doDelete}
          onCancel={() => setOpen(false)}
        />
      )}
    </section>
  );
}
